import React, { useMemo, useState } from 'react';
import DraggableDialog from 'components/common/DraggableDialog';
import { ActionButton } from 'styles/common.styles';
import { theme } from 'styles/theme';
import { isHiGridSystemColumnId } from './core/tanstackGridColumns';
import { buildPivotTable, PIVOT_AGG_OPTIONS } from './core/gridPivot';
import * as XLSX from 'xlsx';

/**
 * 조회/리포트 그리드 — 행/열/값 피벗
 */
export default function GridPivotDialog({
  open,
  onClose,
  fields = [],
  rows = [],
  prefsUserId = '',
  gridId = '',
}) {
  const [rowFields, setRowFields] = useState([]);
  const [colFields, setColFields] = useState([]);
  const [valueField, setValueField] = useState('');
  const [agg, setAgg] = useState('sum');

  const fieldList = useMemo(
    () =>
      (fields || [])
        .filter((f) => f.id && !isHiGridSystemColumnId(f.id))
        .map((f) => ({ id: f.id, label: f.label || f.header || f.id, kind: f.kind })),
    [fields]
  );

  const fieldLabels = useMemo(() => {
    const m = {};
    fieldList.forEach((f) => {
      m[f.id] = f.label;
    });
    return m;
  }, [fieldList]);

  const used = new Set([...rowFields, ...colFields, valueField].filter(Boolean));

  const result = useMemo(
    () =>
      buildPivotTable(rows, {
        rowFields,
        colFields,
        valueField,
        agg,
        fieldLabels,
      }),
    [rows, rowFields, colFields, valueField, agg, fieldLabels]
  );

  const addTo = (zone, id) => {
    if (!id) return;
    if (zone === 'row') {
      setRowFields((p) => (p.includes(id) ? p : [...p, id]));
      setColFields((p) => p.filter((x) => x !== id));
      if (valueField === id) setValueField('');
    } else if (zone === 'col') {
      setColFields((p) => (p.includes(id) ? p : [...p, id]));
      setRowFields((p) => p.filter((x) => x !== id));
      if (valueField === id) setValueField('');
    } else if (zone === 'value') {
      setValueField(id);
      setRowFields((p) => p.filter((x) => x !== id));
      setColFields((p) => p.filter((x) => x !== id));
    }
  };

  const removeFrom = (zone, id) => {
    if (zone === 'row') setRowFields((p) => p.filter((x) => x !== id));
    if (zone === 'col') setColFields((p) => p.filter((x) => x !== id));
    if (zone === 'value') setValueField((v) => (v === id ? '' : v));
  };

  const onDragStart = (e, id) => {
    e.dataTransfer.setData('text/fieldId', id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDropZone = (e, zone) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/fieldId');
    if (id) addTo(zone, id);
  };

  const exportXlsx = () => {
    if (!result.data.length) return;
    const aoa = [result.columns.map((c) => c.header)];
    result.data.forEach((row) => {
      aoa.push(result.columns.map((c) => row[c.id] ?? ''));
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '피벗');
    XLSX.writeFile(wb, `${gridId || 'pivot'}_pivot.xlsx`);
  };

  return (
    <DraggableDialog
      open={open}
      title="피벗"
      onClose={onClose}
      dialogWidth="920px"
      dialogHeight="640px"
      sizeKey={gridId ? `pivot.${gridId}` : 'pivot'}
      prefsUserId={prefsUserId}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          gap: 8,
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.text.primary,
        }}
      >
        <div style={{ display: 'flex', gap: 8, minHeight: 160, flexShrink: 0 }}>
          <Zone title="필드" onDragOver={(e) => e.preventDefault()} onDrop={(e) => e.preventDefault()}>
            {fieldList.map((f) => (
              <span
                key={f.id}
                draggable
                onDragStart={(e) => onDragStart(e, f.id)}
                onDoubleClick={() => addTo('row', f.id)}
                style={{
                  ...chipStyle,
                  opacity: used.has(f.id) ? 0.45 : 1,
                  cursor: 'grab',
                }}
                title="드래그하여 행/열/값에 놓기"
              >
                {f.label}
              </span>
            ))}
          </Zone>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, flex: 1, minHeight: 0 }}>
              <DropZone title="행" onDrop={(e) => onDropZone(e, 'row')}>
                {rowFields.map((id) => (
                  <Chip key={id} label={fieldLabels[id] || id} onRemove={() => removeFrom('row', id)} />
                ))}
              </DropZone>
              <DropZone title="열" onDrop={(e) => onDropZone(e, 'col')}>
                {colFields.map((id) => (
                  <Chip key={id} label={fieldLabels[id] || id} onRemove={() => removeFrom('col', id)} />
                ))}
              </DropZone>
            </div>
            <DropZone title="값" onDrop={(e) => onDropZone(e, 'value')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {valueField ? (
                  <Chip label={fieldLabels[valueField] || valueField} onRemove={() => removeFrom('value', valueField)} />
                ) : (
                  <span style={{ color: theme.colors.text.tertiary }}>숫자 필드를 놓으세요</span>
                )}
                <select
                  value={agg}
                  onChange={(e) => setAgg(e.target.value)}
                  style={{
                    padding: '2px 6px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: 4,
                    fontSize: theme.typography.fontSize.xs,
                  }}
                >
                  {PIVOT_AGG_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </DropZone>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.sm,
          }}
        >
          {!result.data.length ? (
            <div style={{ padding: 16, color: theme.colors.text.tertiary }}>
              행 또는 열에 필드를 놓으면 집계가 표시됩니다. 현재 그리드에 보이는 행이 대상입니다.
            </div>
          ) : (
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              <thead>
                <tr>
                  {result.columns.map((c) => (
                    <th
                      key={c.id}
                      style={{
                        position: 'sticky',
                        top: 0,
                        background: theme.colors.grid.headerBg,
                        border: `1px solid ${theme.colors.grid.border}`,
                        padding: '6px 8px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.data.map((row, i) => (
                  <tr key={i}>
                    {result.columns.map((c, ci) => (
                      <td
                        key={c.id}
                        style={{
                          border: `1px solid ${theme.colors.grid.border}`,
                          padding: '4px 8px',
                          textAlign: ci === 0 ? 'left' : 'right',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {ci === 0
                          ? row[c.id]
                          : row[c.id] === '' || row[c.id] == null
                            ? ''
                            : Number(row[c.id]).toLocaleString('ko-KR', {
                                maximumFractionDigits: 4,
                              })}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, flexShrink: 0 }}>
          <ActionButton type="button" onClick={exportXlsx} disabled={!result.data.length}>
            Excel
          </ActionButton>
          <ActionButton type="button" $variant="primary" onClick={onClose}>
            닫기
          </ActionButton>
        </div>
      </div>
    </DraggableDialog>
  );
}

function Zone({ title, children, ...rest }) {
  return (
    <div
      style={{
        width: 180,
        flexShrink: 0,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius.sm,
        padding: 8,
        overflow: 'auto',
        background: theme.colors.background.light,
      }}
      {...rest}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{children}</div>
    </div>
  );
}

function DropZone({ title, children, onDrop }) {
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={onDrop}
      style={{
        flex: 1,
        minWidth: 0,
        border: `1px dashed ${theme.colors.border}`,
        borderRadius: theme.borderRadius.sm,
        padding: 8,
        background: theme.colors.white,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function Chip({ label, onRemove }) {
  return (
    <span style={chipStyle}>
      {label}
      <button
        type="button"
        onClick={onRemove}
        style={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          padding: 0,
          marginLeft: 4,
          color: theme.colors.text.secondary,
        }}
      >
        ×
      </button>
    </span>
  );
}

const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: theme.borderRadius.pill,
  background: theme.colors.primaryLight,
  color: theme.colors.text.primary,
  fontSize: theme.typography.fontSize.xs,
};
